import { useState, useCallback } from 'react';
import { apiClient } from '../utils/api';
import { toB64Obj } from '../utils/fileUtils';

export const useAivyStudio = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({});
  const [uploadError, setUploadError] = useState(null);

  // AI 설문 생성 API 호출
  const generateAgent = async ({ agent_id, subject, using_feedback }) => {
    setIsGenerating(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/v1/aistudio/create', {
        agent_id,
        subject,
        using_feedback,
      });

      if (response.data.status) {
        return response.data.spec;
      } else {
        throw new Error(response.data.error || '설문 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('Agent generation error:', error);
      
      if (error.response?.data?.error === 'access denied') {
        setError('전문가 계정만 사용할 수 있는 기능입니다.');
        return { accessDenied: true };
      }
      
      setError(error.response?.data?.error || error.message || '설문 생성 중 오류가 발생했습니다.');
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  // 설문 테스트 API 호출
  const testAgent = async ({ spec, input_values }) => {
    setIsTesting(true);
    setError(null);

    try {
      // Go 템플릿 호환성을 위해 템플릿 문법 정리
      const cleanedSpec = {
        ...spec,
        agent_template: spec.agent_template
          .replace(/\{\{#each\s+(\w+)\}\}\{\{\.\}\},?\s*\{\{\/each\}\}/g, '{{.$1}}')
          .replace(/\{\{#if\s+(\w+)\}\}([^{]*)\{\{\.(\w+)\}\}([^{]*)\{\{\/if\}\}/g, '$2{{.$3}}$4')
      };

      // 배열 데이터를 문자열로 변환
      const processedInputValues = { ...input_values };
      Object.keys(processedInputValues).forEach(key => {
        if (Array.isArray(processedInputValues[key])) {
          processedInputValues[key] = processedInputValues[key].join(', ');
        }
      });

      const response = await apiClient.post('/api/v1/aistudio/testing', {
        spec: cleanedSpec,
        input_values: processedInputValues,
      });

      if (response.data.status) {
        return {
          generated_text: response.data.generated_text,
          input_values: input_values, 
        };
      } else {
        throw new Error(response.data.error || '테스트에 실패했습니다.');
      }
    } catch (error) {
      console.error('Agent testing error:', error);
      setError(error.response?.data?.error || error.message || '테스트 중 오류가 발생했습니다.');
      throw error;
    } finally {
      setIsTesting(false);
    }
  };

  const clearError = () => setError(null);

  const handleImageUpload = useCallback(async (key, file) => {
    try {
      if (!file) {
        throw new Error('파일이 선택되지 않았습니다.');
      }

      // 파일 크기 체크 (10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('파일 크기는 10MB를 초과할 수 없습니다.');
      }

      // 파일 형식 체크
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        throw new Error('PNG, JPG, JPEG 형식의 이미지만 업로드 가능합니다.');
      }

      const imageData = await toB64Obj(file);
      setUploadedImages(prev => ({
        ...prev,
        [key]: imageData
      }));
      setUploadError(null);
      return imageData;
    } catch (error) {
      setUploadError(error.message);
      return null;
    }
  }, []);

  const clearUploadedImage = useCallback((key) => {
    setUploadedImages(prev => {
      const newImages = { ...prev };
      delete newImages[key];
      return newImages;
    });
  }, []);

  return {
    generateAgent,
    testAgent,
    isGenerating,
    isTesting,
    error,
    clearError,
    uploadedImages,
    uploadError,
    handleImageUpload,
    clearUploadedImage
  };
}; 